import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import api from '../lib/api';
import {
  ShieldAlert, AlertTriangle, Info, CheckCircle2, RefreshCw,
  TrendingUp, Ticket, FileText,
  Eye, Trash2, CheckCheck, Brain, ExternalLink, Bell,
} from 'lucide-react';

const SEVERITY_CONFIG = {
  critical: { icon: ShieldAlert, color: 'text-red-500', bg: 'bg-red-500/10', border: 'border-red-500/30', label: 'Critical' },
  high: { icon: AlertTriangle, color: 'text-amber-500', bg: 'bg-amber-500/10', border: 'border-amber-500/30', label: 'High' },
  medium: { icon: Info, color: 'text-blue-500', bg: 'bg-blue-500/10', border: 'border-blue-500/30', label: 'Medium' },
  low: { icon: CheckCircle2, color: 'text-emerald-500', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', label: 'Low' },
};

const MODULE_ICONS = {
  sales: TrendingUp, service: Ticket, accounts: FileText,
};

const MODULE_LABELS = {
  sales: 'Sales', service: 'Service', accounts: 'Accounts',
  inventory: 'Inventory', hr: 'HR', fsm: 'Field Service',
};

function formatTime(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  const now = new Date();
  const diff = (now - d) / 1000;
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function AlertCard({ alert, onRead, onDismiss }) {
  const sev = SEVERITY_CONFIG[alert.severity] || SEVERITY_CONFIG.medium;
  const SevIcon = sev.icon;
  const ModIcon = MODULE_ICONS[alert.module] || Brain;
  const navigate = useNavigate();

  const entityUrl = alert.entity_type === 'lead' ? `/leads/${alert.entity_id}`
    : alert.entity_type === 'ticket' ? `/tickets/${alert.entity_id}`
    : alert.entity_type === 'invoice' ? `/invoices/${alert.entity_id}`
    : (alert.action_url === '/meetings' ? '/schedule/meetings' : alert.action_url) || null;

  return (
    <div
      data-testid={`alert-card-${alert.id}`}
      className={`group relative border rounded-xl p-4 transition-all hover:shadow-md ${
        alert.read ? 'bg-muted/30 border-border/50 opacity-75' : `${sev.bg} ${sev.border}`
      }`}
    >
      <div className="flex items-start gap-3">
        {/* Severity Icon */}
        <div className={`p-2 rounded-lg shrink-0 ${sev.bg}`}>
          <SevIcon className={`h-4 w-4 ${sev.color}`} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Badge variant="outline" className="text-xs gap-1 shrink-0">
              <ModIcon className="h-3 w-3" />
              {MODULE_LABELS[alert.module] || alert.module}
            </Badge>
            <Badge variant="outline" className={`text-xs ${sev.color} ${sev.border}`}>
              {sev.label}
            </Badge>
            {!alert.read && (
              <span className="h-2 w-2 rounded-full bg-blue-500 shrink-0" />
            )}
            <span className="text-xs text-muted-foreground ml-auto shrink-0">
              {formatTime(alert.created_at)}
            </span>
          </div>

          <p className="font-semibold text-sm leading-tight">{alert.title}</p>
          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{alert.reasoning}</p>

          {/* Actions */}
          <div className="flex items-center gap-2 mt-3">
            {entityUrl && (
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs gap-1"
                onClick={() => navigate(entityUrl)}
                data-testid={`alert-view-${alert.id}`}
              >
                <ExternalLink className="h-3 w-3" /> View {alert.entity_type}
              </Button>
            )}
            {alert.action_label && (
              <Button size="sm" className="h-7 text-xs" onClick={() => navigate((alert.action_url === '/meetings' ? '/schedule/meetings' : alert.action_url) || entityUrl || '#')}>
                {alert.action_label}
              </Button>
            )}
            {!alert.read && (
              <Button size="sm" variant="ghost" className="h-7 text-xs gap-1 ml-auto" onClick={() => onRead(alert.id)} data-testid={`alert-mark-read-${alert.id}`}>
                <Eye className="h-3 w-3" /> Mark Read
              </Button>
            )}
            <Button size="sm" variant="ghost" className="h-7 text-xs gap-1 text-destructive hover:text-destructive" onClick={() => onDismiss(alert.id)} data-testid={`alert-dismiss-${alert.id}`}>
              <Trash2 className="h-3 w-3" /> Dismiss
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function BrainAlerts() {
  const [alerts, setAlerts] = useState([]);
  const [counts, setCounts] = useState({ total: 0, unread: 0, critical: 0, high: 0 });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filters, setFilters] = useState({ severity: 'all', module: 'all', read: 'all' });

  const fetchAlerts = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filters.severity !== 'all') params.set('severity', filters.severity);
      if (filters.module !== 'all') params.set('module', filters.module);
      if (filters.read === 'unread') params.set('read', 'false');
      if (filters.read === 'read') params.set('read', 'true');

      const res = await api.get(`/api/brain/alerts?${params}`);
      const data = res.data;
      setAlerts(data.alerts || []);
      setCounts(data.counts || {});
    } catch (e) {
      console.error('Failed to fetch alerts:', e);
    }
  }, [filters]);

  const loadData = useCallback(async () => {
    setLoading(true);
    await fetchAlerts();
    setLoading(false);
  }, [fetchAlerts]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAlerts();
    setRefreshing(false);
    toast.success('Alerts refreshed');
  };

  const markRead = async (id) => {
    await api.post(`/api/brain/alerts/${id}/read`);
    setAlerts(prev => prev.map(a => a.id === id ? { ...a, read: true } : a));
    setCounts(prev => ({ ...prev, unread: Math.max(0, prev.unread - 1) }));
  };

  const dismiss = async (id) => {
    await api.post(`/api/brain/alerts/${id}/dismiss`);
    setAlerts(prev => prev.filter(a => a.id !== id));
    setCounts(prev => ({ ...prev, total: Math.max(0, prev.total - 1) }));
    toast.success('Alert dismissed');
  };

  const markAllRead = async () => {
    await api.post(`/api/brain/alerts/read-all`);
    setAlerts(prev => prev.map(a => ({ ...a, read: true })));
    setCounts(prev => ({ ...prev, unread: 0 }));
    toast.success('All alerts marked as read');
  };

  return (
    <div className="space-y-6" data-testid="brain-alerts-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2" data-testid="alerts-page-title">
            <ShieldAlert className="h-7 w-7 text-primary" />
            Brain Alerts — Mission Control
          </h1>
          <p className="text-muted-foreground mt-1">Critical findings detected by your AI Brain</p>
        </div>
        <div className="flex gap-2">
          {counts.unread > 0 && (
            <Button variant="outline" onClick={markAllRead} data-testid="mark-all-read-btn">
              <CheckCheck className="h-4 w-4 mr-2" /> Mark All Read
            </Button>
          )}
          <Button variant="outline" onClick={handleRefresh} disabled={refreshing} data-testid="refresh-alerts-btn">
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-20 rounded-lg" />)}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="alert-stats">
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10"><Bell className="h-5 w-5 text-primary" /></div>
              <div><p className="text-2xl font-bold">{counts.total}</p><p className="text-xs text-muted-foreground">Total Alerts</p></div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10"><Eye className="h-5 w-5 text-blue-500" /></div>
              <div><p className="text-2xl font-bold">{counts.unread}</p><p className="text-xs text-muted-foreground">Unread</p></div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-500/10"><ShieldAlert className="h-5 w-5 text-red-500" /></div>
              <div><p className="text-2xl font-bold">{counts.critical}</p><p className="text-xs text-muted-foreground">Critical</p></div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/10"><AlertTriangle className="h-5 w-5 text-amber-500" /></div>
              <div><p className="text-2xl font-bold">{counts.high}</p><p className="text-xs text-muted-foreground">High Priority</p></div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="w-full sm:w-40">
              <Select value={filters.severity} onValueChange={v => setFilters(p => ({ ...p, severity: v }))}>
                <SelectTrigger data-testid="filter-severity"><SelectValue placeholder="Severity" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Severity</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-full sm:w-40">
              <Select value={filters.module} onValueChange={v => setFilters(p => ({ ...p, module: v }))}>
                <SelectTrigger data-testid="filter-module"><SelectValue placeholder="Module" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Modules</SelectItem>
                  <SelectItem value="sales">Sales</SelectItem>
                  <SelectItem value="service">Service</SelectItem>
                  <SelectItem value="accounts">Accounts</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-full sm:w-40">
              <Select value={filters.read} onValueChange={v => setFilters(p => ({ ...p, read: v }))}>
                <SelectTrigger data-testid="filter-read"><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="unread">Unread Only</SelectItem>
                  <SelectItem value="read">Read</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Alerts List */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-28 rounded-xl" />)}
        </div>
      ) : alerts.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Brain className="h-12 w-12 mx-auto text-muted-foreground/40 mb-4" />
            <h3 className="text-lg font-medium" data-testid="no-alerts">No alerts found</h3>
            <p className="text-muted-foreground mt-1">
              Your AI Brain is monitoring — alerts will appear when it detects something important.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="alerts-list">
          {alerts.map(alert => (
            <AlertCard key={alert.id} alert={alert} onRead={markRead} onDismiss={dismiss} />
          ))}
        </div>
      )}
    </div>
  );
}
